import { DeviceChangeBadge } from 'Modules/softphone/components/SoftphoneDevTools/DeviceChangeBadge';
import { DevToolsSegmentedControl } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsSegmentedControl';
import { DebugEventRow } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugEventRow';
import { DeviceChangeLanes } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugRecord/DebugRecordDevices/DeviceChangeLanes';
import type { DebugRecordDevicesProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugRecord/DebugRecordDevices/index.types';
import {
  CARD_TITLE,
  SURFACE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import {
  formatRelativeLabel,
  getDeviceChangeType,
} from 'Modules/softphone/components/SoftphoneDevTools/utils';
import React, { useCallback, useState } from 'react';
import { cn } from '~/lib/utils';

type TimelineView = 'log' | 'lanes';

/**
 * DebugRecordDevices - The devices the call ran on, and every change during it
 *
 * Changes are stamped relative to the call's start as well as by wall clock:
 * "the mic switched 4s in" is the shape of the report users actually give.
 *
 * Two readings of the same changes: Log opens each one's payload, Lanes places
 * them on elapsed time so a device that flapped looks different from one that
 * was switched twice on purpose.
 */
export const DebugRecordDevices: React.FC<DebugRecordDevicesProps> = ({
  info,
}) => {
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const [view, setView] = useState<TimelineView>('lanes');

  const toggle = useCallback((id: string) => {
    setOpenIds((current) => ({ ...current, [id]: !current[id] }));
  }, []);

  const rows = [
    { label: 'Microphone', value: info.devices?.microphone },
    { label: 'Speaker', value: info.devices?.speaker },
    { label: 'Ringer', value: info.devices?.ringer },
  ];

  const deviceChanges = info.deviceChanges ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className={SURFACE.card}>
        <div className={cn(SURFACE.cardHeader, CARD_TITLE)}>
          Audio devices used
        </div>
        {rows.map(({ label, value }) => (
          <div
            key={label}
            className="flex items-center gap-2.5 border-b border-zinc-800 px-3 py-2.5 last:border-b-0"
          >
            <span className="w-20 flex-shrink-0 text-[11px] text-zinc-400">
              {label}
            </span>
            <span className="min-w-0 flex-1 truncate text-[11.5px] text-zinc-200">
              {value || 'Not captured'}
            </span>
          </div>
        ))}
      </div>

      <div className={SURFACE.card}>
        <div
          className={cn(SURFACE.cardHeader, 'flex items-center gap-2 py-[7px]')}
        >
          <span className={CARD_TITLE}>Device change timeline</span>
          <span className="rounded-[5px] bg-zinc-800 px-1.5 py-px font-sp-mono text-[10.5px] text-zinc-300">
            {deviceChanges.length}
          </span>

          <div className="flex-1" />

          {deviceChanges.length > 0 && (
            <DevToolsSegmentedControl<TimelineView>
              label="Device change timeline view"
              variant="inline"
              value={view}
              onChange={setView}
              options={[
                { value: 'log', label: 'Log' },
                { value: 'lanes', label: 'Lanes' },
              ]}
            />
          )}
        </div>

        {deviceChanges.length === 0 && (
          <p className="px-3 py-2.5 text-[11.5px] text-zinc-400">
            No device changes during this call. A microphone, speaker or ringer
            switched mid-call is recorded here with both device ids.
          </p>
        )}

        {deviceChanges.length > 0 &&
          (view === 'lanes' ? (
            <DeviceChangeLanes
              changes={deviceChanges}
              startTime={info.startTime}
              endTime={info.endTime}
            />
          ) : (
            deviceChanges.map((change) => {
              const deviceType = getDeviceChangeType(change);

              return (
                <DebugEventRow
                  key={change.id}
                  event={change}
                  relativeLabel={formatRelativeLabel(
                    change.timestamp,
                    info.startTime
                  )}
                  /* Falls back to the DEVICE channel badge for a record that
                     never named the device — the row still has to say what it
                     is, and DEV is all that is known. */
                  badge={
                    deviceType ? <DeviceChangeBadge type={deviceType} /> : undefined
                  }
                  isOpen={Boolean(openIds[change.id])}
                  onToggle={() => toggle(change.id)}
                />
              );
            })
          ))}
      </div>
    </div>
  );
};
